<script>
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { componentStore } from "@common/componentStore/componentStore";
import { library } from "@common/library/library";
import { ddStore } from "@common/ddStore";

const editorSettings = {
    generalSettingsPanel: {
        type: 'panel',
        label: 'General Settings',
        id: 'generalSettingsPanel',
        fields: [
            'restrictWorkflowTypes',
        ]
    },
    restrictWorkflowTypes: {
        type: 'CHECKBOX_LIST',
        label: 'Restrict Workflow Types',
        id: 'restrictWorkflowTypes',
        default: {},
        itemsSource: 'configStore.workflowOptions()',
        helpText: 'Pick a workflow type to filter. To show all, leave blank.',
        placeholder: 'Pick a workflow type to filter. To show all, leave blank.'
    },
};

const workflowViewerConfig = {
    cId: "workflowViewer",
    cType: "workflowViewer",
    cTitle: "Workflow Viewer",
    cTemplate: `<div v-if="logicParserReady && !ddLoading && (!isEmptyState || !resolvedHideWhenEmpty)" :style="{ 'max-width': maxWidth }">
        <div>
            <div v-if="assignmentList && !assignmentsLoaded" class="workflow-loader" style="padding: 15px;">
                <div class="loader-animation-container">
                    <div class="loader-animation">
                        <div class="ball"></div>
                        <div class="ball"></div>
                        <div class="ball"></div>
                        <div class="ball"></div>
                        <div class="ball"></div>
                    </div>
                </div>
                <p>{{ ddStore['WORKFLOW']['WORKFLOW_LOADING_ASSIGNMENTS_MESSAGE'] }}</p>
            </div>
            <div v-else-if="!workflowExists" class="no-workflow">
                <p>{{ ddStore['WORKFLOW']['WORKFLOW_NO_WORKFLOWS_WARNING'] }}</p>
            </div>
            <div v-else-if="!hasAssignments" class="no-workflow">
                <p>{{ ddStore['WORKFLOW']['WORKFLOW_NO_ASSIGNMENTS_WARNING'] }}</p>
            </div>
            <div v-else-if="!workflowTabs.length">
                <p>{{ ddStore['WORKFLOW']['WORKFLOW_LOADING_WORKFLOW_TABS_MESSAGE'] }}</p>
            </div>
            <div v-if="workflowTabs.length">
                <div v-if="isWorkflowFullyLoadTimedout && !childPendingSubscriptionsLength" class="hidden-workflows-toggle">
                    <control name="SHOW_HIDDEN_WORKFLOWS"></control>
                </div>
                <div v-if="!visibleTabs" class="workflow-loader" style="padding: 15px;">
                    <div class="loader-animation-container" v-if="waitingFirstWorkflowLoad || childPendingSubscriptionsLength || !isWorkflowFullyLoadTimedout">
                        <div class="loader-animation">
                            <div class="ball"></div>
                            <div class="ball"></div>
                            <div class="ball"></div>
                            <div class="ball"></div>
                            <div class="ball"></div>
                        </div>
                    </div>
                    <p v-if="waitingFirstWorkflowLoad">{{ ddStore['WORKFLOW']['WORKFLOW_LOADING_WORKFLOWS_MESSAGE'] }}</p>
                    <p v-if="!isWorkflowFullyLoadTimedout">{{ ddStore['WORKFLOW']['WORKFLOW_LOADING_WORKFLOWS_TIMEOUT_MESSAGE'] }}</p>
                    <p v-else-if="!childPendingSubscriptionsLength && !resolvedHideWhenEmpty">{{ ddStore['WORKFLOW']['WORKFLOW_NO_VISIBLE_WORKFLOWS_WARNING'] }}</p>
                </div>
                <div v-show="!waitingFirstWorkflowLoad">
                    <tab-list :cId="'tabList_workflow__' + cId" :prop-tabs-list="workflowTabs" :prop-selected-tab="selectedTab" @update:selectedTab="changeSelectedTab">
                        <template v-for="(tab, index) in workflowTabs" #[tab.slotTemplateId]>
                            <div class="workflow-header">
                                <div class="workflow-loader">
                                    <div class="loader-animation-container" v-show="!tab?.workflowTabsError" v-if="loaderVisible">
                                        <div class="loader-animation">
                                            <div class="ball"></div>
                                            <div class="ball"></div>
                                            <div class="ball"></div>
                                            <div class="ball"></div>
                                            <div class="ball"></div>
                                        </div>
                                    </div>
                                    <p v-show="!tab?.workflowTabsError && !workflowTabs[tab.tabIndex]">
                                        {{ ddStore['WORKFLOW']['WORKFLOW_LOADING_WORKFLOW_DETAILS_MESSAGE'] }}
                                    </p>
                                    <p v-show="!tab?.workflowTabsError && childPendingSubscriptionsLength">
                                        {{ ddStore['WORKFLOW']['WORKFLOW_LOADING_STEP_DETAILS_MESSAGE'] }}
                                    </p>
                                </div>
                                <div class="workflow-status" v-if="ecUserProperty?.CLIENT_ROLE_OPTION">
                                    <span class="assignment-id">(Assignment: {{tab.assignmentId}})</span>
                                    Workflow Status: {{assignmentList?.[tab.assignmentId]?.AFF_ASSIGNMENT_WORKFLOW_STATUS?.[tab.workflowId]}}
                                    <button v-if="isAdminRole" type="button" class="workflow-action-button" title="Update Status"
                                        @click="actionStore.updateWorkflowStatus({
                                        assignmentId: tab.assignmentId,
                                        workflowId: tab.workflowId})"><span class="h-button-text">Update Status</span>
                                    </button>
                                </div>
                            </div>
                            <div v-show="tab?.workflowTabsError">
                                <p>{{ tab.workflowTabsError }}</p>
                            </div>
                            <div v-if="workflowTabs[tab.tabIndex]" v-show="!tab?.workflowTabsError">
                                <div class="workflow-step-list" v-show="selectedTab === tab.tabIndex && tab.show">
                                    <div v-for="(activity, index) in tab.activities" :key="index">
                                        <activity-step-panel
                                            c-type="activityStepPanel"
                                            :c-id="'activityStepPanel__' + cId + '_' + uniqueId + '_' + activity.ACTIVITY_ENTITY_ID + '_' + (activity.AFFILIATION_ID || activity?.record?.AFFILIATION_ID)"
                                            :ref="'activityStepPanel__' + cId + '_' + uniqueId + '_' + activity.ACTIVITY_ENTITY_ID + '_' + (activity.AFFILIATION_ID || activity?.record?.AFFILIATION_ID)"
                                            :record="activity"
                                            :workflow="tab"
                                            :context-vol-id="activity?.VOLUNTEER_ID || activity?.record?.VOLUNTEER_ID"
                                            :context-aff-id="activity?.AFFILIATION_ID || activity?.record?.AFFILIATION_ID"
                                            :context-ae-id="activity?.ACTIVITY_ENTITY_ID || activity?.record?.ACTIVITY_ENTITY_ID"
                                            :workflow-order-index="index"
                                            :prop-editor-visible="editorVisible"
                                            @ready="onStepReady"
                                        ></activity-step-panel>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </tab-list>
                </div>
            </div>
        </div>
    </div>`,
    controls: [
        {
            name: 'SHOW_HIDDEN_WORKFLOWS',
            label: 'Show Hidden Workflows',
            type: 'boolean',
            inlineLabelReverse: true,
            logic: [{
                criteria: '!!this.component.ecUserProperty.CLIENT_ROLE_OPTION',
                prop: 'display',
                valueRecord: 'self',
                valueType: 'criteria'
            }],
            onchange: 'loadWorkflowTabs'
        }
    ]
};

const workflowViewerCmp = {
    name: "workflowViewer",
    editorSettings: editorSettings,
    props: {
        editorWorkflowSettings: {
            type: Object
        },
        workflowId: {
            default: false
        },
        editorVisible: {
            type: Boolean,
            default: false
        },
        hideWhenEmpty: {
            default: undefined
        }
    },
    data() {
        return {
            isRecruiter: (typeof VUE_PROJECT_NAME !== 'undefined' && VUE_PROJECT_NAME === 'recruiter')
                || (typeof PROJECT_NAME !== 'undefined' && PROJECT_NAME === 'recruiter'),
            uniqueId: library.getUniqueID(),
            targetedItem: false,
            workflowTabs: [],
            waitingFirstWorkflowLoad: true,
            affFields: [
                'AFFILIATION_ID',
                'AFF_COMPLIANCE_STATUS_ID',
                'AFF_STATUS',
                'AFF_EXPIRING_AFFILIATION_ID'
            ],
            user: false,
            hasAssignments: false,
            logicParserReady: false,
            selectedTab: null,
            workflowStepStatus: {},
            isWorkflowFullyLoadTimedout: false,
            assignmentsLoaded: false,
            workflowExists: false,
            childPendingSubscriptions: [],
            childPendingSubscriptionsLength: 0,
            requestSettings: {
                startDate: library.getMoment().subtract(1, 'day').format('MM/DD/YYYY'),
                endDate: library.getMoment().add(60, 'days').format('MM/DD/YYYY'),
            },
            ddLoading: true
        };
    },
    watch: {
        workflowStepStatus: {
            handler() {
                this.updateTabVisibility();
            },
            deep: true
        },
        contextOrgId(orgId) {
            const orgTab = this.workflowTabs.find((tab) => tab.orgId === orgId) || this.workflowTabs[0];
            // this.workflowTabs.forEach((tab) => { tab.show = tab.orgId === orgId || !orgId; });
            this.updateTabVisibility();
            this.changeSelectedTab(orgTab?.tabIndex);
        }
    },
    computed: {
        maxWidth() {
            let isFromCoordinatorProfile = document.getElementById('profileTabContent');
            return isFromCoordinatorProfile ? '90vw' : false;
        },
        isAdminRole() {
            const coordinator = this.userStore.coordinator;
            return coordinator?.CLIENT_IS_ADMIN === 'Yes' || coordinator?.CLIENT_IS_STAFF === 'Yes';
        },
        assignmentList() {
            const ORG_PSP = 3;
            const AFF_PSP = 32;
            if (!this.contextVolId || this.contextVolId <= 0) return {};
            let assignmentsSub = this.recordStore.subscribe({
                source: ['sds_getAssignmentList'],
                updateFrequency: 360,
                params: {
                    volId: this.contextVolId,
                    fields: { [ORG_PSP]: ['ORG_TITLE', 'ORGANIZATION_ID', 'WORKFLOW_ID', 'WORKFLOW_TITLE'], [AFF_PSP]: ['AFF_ASSIGNMENT_WORKFLOW_STATUS', 'ENTRY_POINT_ORG_ID'] },
                    // fields: { 3: ['ORG_TITLE', 'ORGANIZATION_ID', 'WORKFLOW_ID', 'WORKFLOW_TITLE'], 32: ['AFF_ASSIGNMENT_WORKFLOW_STATUS', 'AFF_STATUS_ID', 'ENTRY_POINT_ORG_ID'] },
                    params: { strictTypeResults: true }
                },
                sDsParams: {
                    errors: {
                        show: true,
                        toastTimeout: 3,
                        toastMessage: 'Failed to load assignments, reloading...',
                        report: true,
                        forceReloadCount: 1
                    }
                }
            }, this.cId);

            if (assignmentsSub.lastRequestTime) {
                this.assignmentsLoaded = true; // Only sets to true on first loaded, not on polling updates
            }

            let records = library.cloneObj(this.recordStore.getSubscription(assignmentsSub.key));
            if (!library.objIsEmpty(records)) {
                setTimeout(() => {
                    this.hasAssignments = true;
                    this.loadWorkflowTabs();
                }, 10);
            }
            return records;
        },
        visibleTabs() {
            return this.workflowTabs.find((tab) => tab.show);
        },
        resolvedHideWhenEmpty() {
            if (this.propHideWhenEmpty !== undefined) return this.propHideWhenEmpty;
            return this.isRecruiter;
        },
        isEmptyState() {
            return this.isWorkflowFullyLoadTimedout
                && !this.childPendingSubscriptionsLength
                && !this.waitingFirstWorkflowLoad
                && this.workflowTabs.length
                && !this.visibleTabs;
        },
        loaderVisible() {
            return this.workflowTabs[this.selectedTab].loading
                || this.childPendingSubscriptionsLength;
        }
    },
    methods: {
        async onCreate() {
            await library.prepLogicParsingToFront();
            this.logicParserReady = true;
        },
        async onLoad() {
            this.ddLoading = true;
            await ddStore.preloadGroup('WORKFLOW');
            this.ddLoading = false;
        },
        changeSelectedTab(tabIndex) {
            this.selectedTab = tabIndex;
            this.loadWorkflowDetails(tabIndex);
        },
        getAssignmentOrgs(assignment) {
            if (!assignment?.LINKED_RECORDS) return [];
            const assignmentOrgDetails = {};

            const orgs = Object.values(
                library.filterObj(
                    assignment.LINKED_RECORDS,
                    ['VOL', 'OPP', 'ORGANIZATIONS'],
                    true
                )
            );

            orgs.forEach((org) => {
                let wfRecord = this.recordStore.records.org[org];
                if (!wfRecord) return;
                const normalizedIds = [].concat(wfRecord.WORKFLOW_ID || []);
                assignmentOrgDetails[org] = {
                    ORG_ID: +org,
                    ORG_TITLE: wfRecord.ORG_TITLE,
                    WORKFLOW_IDS: normalizedIds,
                    WORKFLOW_TITLE: wfRecord.WORKFLOW_TITLE
                };
            });

            return assignmentOrgDetails;
        },
        getRestrictedWorkflowTypes() {
            const restrictTypeObj = this.config?.restrictWorkflowTypes;
            if (!restrictTypeObj) return [];

            const restrictTypes = Object.entries(restrictTypeObj);
            return restrictTypes
                .filter((workflowOption) => workflowOption[1])
                .map((workflowOption) => Number(workflowOption[0]));
        },
        setTabVisibility(workflow) {
            let show = false;
            const hiddenStatuses = ['Completed', 'Not Started', 'Waiting', 'Transferred'];
            let status = this.assignmentList?.[workflow.assignmentId]?.AFF_ASSIGNMENT_WORKFLOW_STATUS?.[workflow.workflowId]
                || workflow.workflowStatus
                || 'Not Started';
            if (!hiddenStatuses.includes(status)) show = true;

            if (status === 'Waiting') {
                // show = true;
                // TODO: Move up if this true for all enabled workflows?
                let tabSteps = this.workflowStepStatus?.[workflow.wfTabId];

                if (tabSteps.loaded && tabSteps.enabled) {
                    show = true;
                }

                if (!show && workflow?.affiliations) {
                    for (let iAffId in workflow.affiliations) {
                        if (iAffId === 'length') continue;
                        let iAffiliation = workflow.affiliations[iAffId];
                        // expiring or expired or in progress and prev
                        if ((iAffiliation.AFF_IS_ENABLED && !iAffiliation.AFF_IS_COMPLETED)
                            || iAffiliation.AFF_COMPLIANCE_STATUS_ID === 3
                            || iAffiliation.AFF_COMPLIANCE_STATUS_ID === 1
                            || (iAffiliation.AFF_EXPIRING_AFFILIATION_ID && iAffiliation.AFF_STATUS === 3)
                        ) {
                            show = true;
                            break;
                        }
                    }
                }
            }
            if (this?.controls?.SHOW_HIDDEN_WORKFLOWS.value) show = true;
            return show;
        },
        generateWorkflowTitle(wfTitle, location = false, program = false) {
            let _title = '<span class="workflow-title">' + wfTitle + '</span> for ';
            if (location && program) {
                _title = _title + '<span class="program-title">' + program + '</span>'
                    + '<br>at <span class="location-title">' + location + '</span>';
            }
            else if (location) {
                _title = _title + '<span class="location-title">' + location + '</span>';
            }
            else if (program) {
                _title = _title + '<span class="program-title">' + program + '</span>';
            }
            return _title;
        },
        mapActivities({
            details, order, affiliations, tabIndex, assignmentId, workflowId
        }) {
            if (!details || !order || !affiliations) return false;
            return order
                .map((id) => {
                    // details[id].isComponentItem = true;
                    // details[id].itemComponentId = 'activityStepPanel';
                    details[id].AFFILIATION_ID = affiliations[id];
                    details[id].tabIndex = tabIndex;
                    details[id].assignmentId = assignmentId;
                    details[id].workflowId = workflowId;
                    details[id].WORKFLOW_ID = workflowId;
                    details[id].VOLUNTEER_ID = this.volId;
                    details[id].volId = this.volId;
                    return details[id];
                })
                .filter((item) => item.AFFILIATION_ID !== 0);
        },
        loadWorkflowTabs() {
            if (this.editorWorkflowConfig || library.objIsEmpty(this.assignmentList)) {
                return this.workflowTabs;
            }

            let tabIndex = 0;
            let waitingTabs = [];

            Object.values(this.assignmentList).forEach((assignment) => {
                let assignmentOrgDetails = this.getAssignmentOrgs(assignment);
                const restrictTypes = this.getRestrictedWorkflowTypes();

                // Get Entry Point Details
                const [id1, id2] = assignment?.ENTRY_POINT_ORG_ID ?? [];

                let entryOrg1 = this.recordStore.records.org[id1]?.ORG_TITLE;
                let entryOrg2 = this.recordStore.records.org[id2]?.ORG_TITLE;

                for (const orgId in assignmentOrgDetails) {
                    const org = assignmentOrgDetails[orgId];

                    if (!org.WORKFLOW_IDS.length) continue;
                    if (!entryOrg2) entryOrg2 = org.ORG_TITLE;

                    this.workflowExists = true;

                    // eslint-disable-next-line no-loop-func
                    org.WORKFLOW_IDS.forEach((workflowId, idsIndex) => {
                        const wfTabId = assignment.ID + '_' + workflowId;

                        if (this.workflowTabs.some((tab) => tab.wfTabId === wfTabId)) return;
                        if (restrictTypes.length && !restrictTypes.includes(workflowId)) return;
                        let currWorkflowTitle = org.WORKFLOW_TITLE[idsIndex];
                        const title = this.generateWorkflowTitle(currWorkflowTitle, entryOrg1, entryOrg2);
                        const workflowStatus = assignment?.AFF_ASSIGNMENT_WORKFLOW_STATUS?.[workflowId] || 'Not Started';

                        if (!this.workflowStepStatus[wfTabId]) this.workflowStepStatus[wfTabId] = {};

                        let newTab = {
                            ASSIGNMENT_DETAIL_RECORD: assignment?.ENTRY_POINT_ORG_ID,
                            ASSIGNMENT: assignment,
                            title,
                            useVueSlot: true,
                            slotTemplateId: 'tabList_item_' + tabIndex,
                            assignmentId: assignment.ID,
                            orgId: org.ORG_ID,
                            // assignmentOrgDetails,
                            show: false,
                            workflowId,
                            wfTabId,
                            workflowStatus,
                            tabIndex,
                            loading: true
                        };

                        if (workflowStatus === 'Waiting') waitingTabs.push(newTab);
                        else {
                            this.workflowTabs.push(newTab);
                            tabIndex++;
                            // if ((newTab.show) && (this.selectedTab === tabIndex || this.selectedTab === null)) {
                            //     this.changeSelectedTab(tabIndex);
                            // }
                        }
                    });
                }
            });
            // Add waiting workflows to the end
            waitingTabs.forEach((tab) => {
                tab.tabIndex = tabIndex;
                tab.slotTemplateId = 'tabList_item_' + tabIndex;
                this.workflowTabs.push(tab);
                this.loadWorkflowDetails(tabIndex); // NOTE: We must load the workflow to determine if it should show
                tabIndex++;
            });
            this.waitingFirstWorkflowLoad = false;
            this.updateTabVisibility();

            return true;
        },
        loadWorkflowDetails(tabIndex) {
            const AFF_PSP = 32;
            const tabDetails = library.cloneObj(this.workflowTabs[tabIndex]);

            if (typeof tabDetails !== 'object') return;

            let { assignmentId, workflowId } = tabDetails;

            if (!tabDetails.workflowTabRequests || tabDetails.workflowTabRequests === 'failure') {
                this.workflowTabs[tabIndex].workflowTabRequests = 'pending';
                // library.showLoadingIndicator();
                let volId = this.contextVolId || "";
                let wfRequestFields = {
                    [AFF_PSP]: this.affFields
                };
                this.sDs.post('sds_getWorkflowDetails', volId, assignmentId, workflowId, wfRequestFields, { strictTypeResults: true })
                    .then((response) => {
                        if (response.status === 'success' && response.data) {
                            let resData = response.data;
                            let tmpWorkflow = resData?.WORKFLOW?.[workflowId];
                            if (!tmpWorkflow) {
                                tabDetails.workflowTabsError = ddStore.getAliasSync('WORKFLOW', 'WORKFLOW_HAS_NO_DATA_WARNING');
                            }
                            tabDetails.affiliations = resData.AFFILIATION;
                            tabDetails.activities = this.mapActivities({
                                details: resData.ACTIVITY_ENTITY,
                                order: tmpWorkflow?.ACTIVITY_ENTITY_ID,
                                affiliations: tmpWorkflow?.VOL_AE_AFFILIATION_ID,
                                tabIndex,
                                workflowId,
                                assignmentId
                            });
                            tabDetails.WORKFLOW_DISABLE_AUTO_STATUS_UPDATES = tmpWorkflow?.WORKFLOW_DISABLE_AUTO_STATUS_UPDATES;
                            if (!tabDetails.activities.length) {
                                tabDetails.workflowTabsError = ddStore.getAliasSync('WORKFLOW', 'WORKFLOW_NO_INCLUDED_ACTIVITIES_WARNING');
                            }
                        }

                        library.hideLoadingIndicator();
                        tabDetails.workflowTabRequests = response.status;

                        this.workflowTabs[tabIndex] = tabDetails; // Note: this is passed to activityStepPanel as "record"
                    });
            }
        },
        updateTabVisibility() {
            let contextOrg = this.orgId;
            let selectedTabVisible = false;
            let firstVisibleTab = false;

            this.workflowTabs.forEach((wfTab) => {
                let show = this.setTabVisibility(wfTab);

                if (contextOrg && wfTab.orgId !== contextOrg) show = false;
                wfTab.show = show;
                if (show) {
                    if (this.selectedTab === wfTab.tabIndex) selectedTabVisible = true;
                    if (firstVisibleTab === false) firstVisibleTab = wfTab.tabIndex;
                }
            });

            if (!selectedTabVisible && firstVisibleTab !== false) {
                this.changeSelectedTab(firstVisibleTab);
            }

            // TODO Remove this temporary fix after we get subscription tracking under control
            if (!this.isWorkflowFullyLoadTimedout) {
                setTimeout(() => {
                    this.isWorkflowFullyLoadTimedout = true;
                }, 15000);
            }
        },
        onStepReady(step) {
            const aeId = step.ACTIVITY_ENTITY_ID;
            const tabIndex = step.tabIndex;
            const workflowTab = this.workflowTabs[tabIndex];

            let matchingStep = workflowTab.activities.find((activity) => activity.ID === aeId);
            matchingStep.loaded = step.loaded;
            matchingStep.include = step.include;
            matchingStep.enable = step.enable && !step.complete;
            matchingStep.complete = step.complete;

            if (workflowTab.activities.every((activity) => activity.loaded)) {
                let tabStatuses = this.workflowStepStatus[workflowTab.wfTabId];
                tabStatuses.loaded = true;
                tabStatuses.included = workflowTab.activities.filter((step) => step.include).length;
                tabStatuses.enabled = workflowTab.activities.filter((step) => step.enable).length;
                tabStatuses.completed = workflowTab.activities.filter((step) => step.complete).length;
                this.workflowTabs[tabIndex].loading = false;
                // Set errors
                workflowTab.workflowTabsError = tabStatuses.included
                    ? 0
                    : ddStore.getAliasSync('WORKFLOW', 'WORKFLOW_NO_INCLUDED_ACTIVITIES_WARNING');
            }
        }
    },
    components: {
        tabList: defineAsyncComponent(() => import('@common/tabList/tabList.vue'))
    }
};

componentStore.initComponent(workflowViewerCmp, workflowViewerConfig);

export default workflowViewerCmp;
</script>
<style>
/* Loader TODO: Move this to the loader component */
.loader-animation-container {
    display: inline-block;
    width: 60px;
}
.loader-animation {
    display: grid;
    grid-template-columns: repeat(5, 10px);
    gap: 3px;
    justify-content: center;
    align-items: center;
}
.ball {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    animation: wave 1.5s ease-in-out infinite;
    background-color:#EC135E;
}
.ball:nth-child(2) {
    animation-delay: -0.2s;
    background-color:#EF7D22;
}
.ball:nth-child(3) {
    animation-delay: -0.4s;
    background-color:#F7CE46;
}
.ball:nth-child(4) {
    animation-delay: -0.6s;
    background-color:#8BA93D;
}
.ball:nth-child(5) {
    animation-delay: -0.8s;
    background-color:#00ADC5;
}
@keyframes wave {
    0%, 100% {
        transform: translateY(6px);
    }
    50% {
        transform: translateY(-6px);
    }
}
/* End Loader */
</style>
<style scoped>
.workflow-header {
    display: flex;
    padding: 5px;
    border-bottom: 1px solid #b0b0b0;
    color: #383c50;
}
.workflow-loader p {
    display: inline-block;
    line-height: 24px;
    font-size: 16px;
    margin: 0 0 0 15px;
    vertical-align: top;
}
.no-workflows-message {
    font-size: 16px;
    margin-top: 20px;
}
.workflow-status {
    text-align: right;
    flex-grow: 9;
    font-size: 16px;
    font-weight: bold;
}
.assignment-id {
    font-size: 14px;
}
:deep(.step-not-visible) {
    display: none;
}
.workflow-step-list:has(.hide-other-steps) :deep(.activity-step-panel:not(.hide-other-steps)) {
    display: none;
}
@keyframes vibrate {
    0%, 96.9% { transform: rotate(0deg) translateX(0); }
    97.0% { transform: rotate(-3deg) translateX(-2px); }
    97.1% { transform: rotate(3deg)  translateX(2px); }
    97.2% { transform: rotate(-3deg) translateX(-2px); }
    97.3% { transform: rotate(3deg)  translateX(2px); }
    97.4% { transform: rotate(-2.5deg) translateX(-1.8px); }
    97.5% { transform: rotate(2.5deg)  translateX(1.8px); }
    97.6% { transform: rotate(-2.5deg) translateX(-1.8px); }
    97.7% { transform: rotate(2.5deg)  translateX(1.8px); }
    97.8% { transform: rotate(-2deg) translateX(-1.5px); }
    97.9% { transform: rotate(2deg)  translateX(1.5px); }
    98.0% { transform: rotate(-2deg) translateX(-1.5px); }
    98.1% { transform: rotate(2deg)  translateX(1.5px); }
    98.2% { transform: rotate(-1.5deg) translateX(-1px); }
    98.3% { transform: rotate(1.5deg)  translateX(1px); }
    98.4% { transform: rotate(-1.5deg) translateX(-1px); }
    98.5% { transform: rotate(1.5deg)  translateX(1px); }
    98.6% { transform: rotate(-1deg) translateX(-0.8px); }
    98.7% { transform: rotate(1deg)  translateX(0.8px); }
    98.8% { transform: rotate(-0.5deg) translateX(-0.5px); }
    98.9% { transform: rotate(0.5deg)  translateX(0.5px); }
    99%, 100% { transform: rotate(0deg) translateX(0); }
}
.workflow-action-button {
    border: 0 none;
    transition: background .1s ease-in;
    background: rgba(255, 255, 255, .20);
    border-radius: 15px;
    color: #2b2b86;
    padding: 4px 12px;
    margin: 0 3px 0 0;
    text-decoration: underline;
}
.workflow-action-button:hover {
    background: rgba(0, 0, 0, .08);
    transition: all .1s ease-in-out;
}

</style>
